import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, ClipboardList, Building2, HardHat, Truck, Package, Download, Zap, CheckCircle2, ArrowLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, downloadCsv } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import RapportinoModal from "@/components/RapportinoModal";
import RapportinoView from "@/components/RapportinoView";

export default function Rapportini() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [selectedCantiere, setSelectedCantiere] = useState("");
  const [modal, setModal] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toGenera, setToGenera] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!selectedCantiere) { setItems([]); return; }
    setLoading(true);
    const params = selectedCantiere !== "all" ? { cantiere_id: selectedCantiere } : undefined;
    setItems(await api.get("/rapportini", params));
    setLoading(false);
  }, [selectedCantiere]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const [c, o, m, f] = await Promise.all([
        api.get("/cantieri"), api.get("/operai"), api.get("/mezzi"), api.get("/fornitori"),
      ]);
      setCantieri(c); setOperai(o); setMezzi(m); setFornitori(f);
    })();
  }, []);

  const confirmDelete = async () => {
    try {
      await api.del(`/rapportini/${toDelete.id}`);
      toast.success("Rapportino spostato nel cestino.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const confirmGenera = async () => {
    try {
      const res = await api.post(`/rapportini/${toGenera.id}/genera-costi`);
      toast.success(`Create ${res.created} transazioni di uscita (${eur(res.totale)}).`);
      setToGenera(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const exportCsv = async () => {
    try {
      const params = selectedCantiere !== "all" ? { cantiere_id: selectedCantiere } : undefined;
      await downloadCsv("/rapportini/export", "rapportini.csv", params);
      toast.success("Rapportini esportati in CSV.");
    } catch { toast.error("Esportazione non riuscita."); }
  };

  return (
    <div className="space-y-6">
      {!selectedCantiere ? (
        <>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Rapportini</h1>
            <p className="text-sm text-muted-foreground mt-1">Seleziona un cantiere per vedere e gestire i suoi rapportini giornalieri.</p>
          </div>
          {cantieri.length === 0 ? (
            <Card><CardContent className="py-16 text-center">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-medium">Nessun cantiere</p>
              <p className="text-sm text-muted-foreground">Crea prima un cantiere in "Cantieri &amp; Commesse".</p>
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <button data-testid="select-cantiere-all" onClick={() => setSelectedCantiere("all")}
                className="group flex items-center justify-between gap-3 rounded-xl border-2 border-dashed border-border bg-card px-5 py-5 text-left transition-all hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-950/10">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary shrink-0"><ClipboardList className="h-5 w-5" /></div>
                  <div className="min-w-0"><p className="font-semibold">Tutti i cantieri</p><p className="text-xs text-muted-foreground">Vedi tutti i rapportini</p></div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-amber-600 shrink-0" />
              </button>
              {cantieri.map((c) => (
                <button key={c.id} data-testid={`select-cantiere-${c.id}`} onClick={() => setSelectedCantiere(c.id)}
                  className="group flex items-center justify-between gap-3 rounded-xl border-2 border-border bg-card px-5 py-5 text-left transition-all hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-950/10 animate-fade-up">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 shrink-0"><Building2 className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.codice_commessa ? `${c.codice_commessa} · ` : ""}{c.cliente || "—"}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-amber-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" data-testid="rapportini-back-button" onClick={() => setSelectedCantiere("")}><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{selectedCantiere === "all" ? "Tutti i cantieri" : (cantieri.find((c) => c.id === selectedCantiere)?.nome || "Rapportini")}</h1>
                <p className="text-sm text-muted-foreground mt-1">Rapportini giornalieri di manodopera, mezzi e subappalti.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportCsv} data-testid="export-rapportini-button" className="gap-2">
                <Download className="h-4 w-4" /> <span className="hidden sm:inline">Esporta CSV</span>
              </Button>
              <Button onClick={() => { setEditing(null); setModal(true); }} data-testid="add-rapportino-button"
                className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
                <Plus className="h-4 w-4" /> Nuovo Rapportino
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2">{[1, 2].map((i) => <div key={i} className="h-48 rounded-xl bg-secondary animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <Card><CardContent className="py-16 text-center">
              <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-medium">Nessun rapportino</p>
              <p className="text-sm text-muted-foreground">Crea il primo rapportino giornaliero per questo cantiere.</p>
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {items.map((r) => (
                <Card key={r.id} data-testid={`rapportino-card-${r.id}`} className="animate-fade-up">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 text-sm font-semibold truncate"><Building2 className="h-4 w-4 text-amber-600 shrink-0" />{r.cantiere_nome}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(r.data)}</p>
                        {r.costi_generati && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium"><CheckCircle2 className="h-3 w-3" /> Costi generati</span>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-rapportino-${r.id}`} onClick={() => setViewing(r)}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-rapportino-${r.id}`} onClick={() => { setEditing(r); setModal(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                        {isOwner && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-rapportino-${r.id}`} onClick={() => setToDelete(r)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </div>

                    {r.descrizione_lavori && <p className="text-sm text-muted-foreground line-clamp-2">{r.descrizione_lavori}</p>}

                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><HardHat className="h-3.5 w-3.5" />{r.operai?.length || 0} operai</span>
                      <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><Truck className="h-3.5 w-3.5" />{r.mezzi?.length || 0} mezzi</span>
                      <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><Package className="h-3.5 w-3.5" />{r.subappaltatori?.length || 0} subappalti</span>
                    </div>

                    <div className="grid grid-cols-4 gap-2 border-t border-border pt-3 text-center">
                      <div><p className="text-[10px] uppercase text-muted-foreground">Manod.</p><p className="text-sm font-mono font-semibold">{eur(r.totali?.costo_manodopera)}</p></div>
                      <div><p className="text-[10px] uppercase text-muted-foreground">Mezzi</p><p className="text-sm font-mono font-semibold">{eur(r.totali?.costo_mezzi)}</p></div>
                      <div><p className="text-[10px] uppercase text-muted-foreground">Subapp.</p><p className="text-sm font-mono font-semibold">{eur(r.totali?.costo_subappalti)}</p></div>
                      <div><p className="text-[10px] uppercase text-muted-foreground">Totale</p><p className="text-sm font-mono font-bold text-amber-600">{eur(r.totali?.costo_totale)}</p></div>
                    </div>

                    <Button variant="outline" size="sm" className="w-full gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-300 dark:hover:bg-amber-950/30"
                      data-testid={`genera-costi-${r.id}`} onClick={() => setToGenera(r)}>
                      <Zap className="h-3.5 w-3.5" /> {r.costi_generati ? "Rigenera costi di uscita" : "Genera costi di uscita"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <RapportinoModal open={modal} onOpenChange={setModal} cantieri={cantieri} operai={operai} mezzi={mezzi} fornitori={fornitori} initial={editing} onSaved={load} />
      <RapportinoView open={Boolean(viewing)} onOpenChange={(o) => !o && setViewing(null)} rapportino={viewing} />

      <AlertDialog open={Boolean(toGenera)} onOpenChange={(o) => !o && setToGenera(null)}>
        <AlertDialogContent data-testid="genera-costi-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Generare i costi di uscita?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno create transazioni di uscita sul cantiere "{toGenera?.cantiere_nome}" per manodopera, mezzi e subappalti
              di questo rapportino ({eur(toGenera?.totali?.costo_totale)}).
              {toGenera?.costi_generati && " Le transazioni generate in precedenza da questo rapportino verranno sostituite."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGenera} data-testid="confirm-genera-costi"
              className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
              Genera costi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Spostare nel cestino?</AlertDialogTitle>
            <AlertDialogDescription>Il rapportino potrà essere ripristinato dal Cestino.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
